import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import Select from "react-select";
import moment from "moment";
import Backend from "../../../../Backend/Backend";
import toast from "react-hot-toast";

const backend = await new Backend().init();

export default function WatchlistHeader({
  onSearch,
  onLoad,
  onChangeLink,
  watchlistType,
  symbolsList,
  nse_future_active,
  nse_cash_active,
  mcx_future_active,
  mcx_options_active,
  nse_options_active,
  crypto_active,
  comex_active,
  forex_active,
  us_stock_active,
  sgx_active,
  index_active,
  dgcx_active,
  ncdex_active,
}) {
  const [query, setQuery] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Refs for focusing
  const selectRef = useRef(null);
  const searchInputRef = useRef(null);

  // Handle search input
  const handleSearchChange = (e) => {
    setQuery(e.target.value);
    onSearch?.(e.target.value);
  };

  // Handle dropdown change
  const handleChange = (option) => {
    setSelectedOption(option);
    if (handleAddSymbol) handleAddSymbol(option);
  };

  // Add symbol API call
  const handleAddSymbol = useCallback(
    async (option) => {
      if (isLoading) return;
      setIsLoading(true);

      try {
        const data = {
          server: localStorage.getItem("server"),
          id: localStorage.getItem("id"),
          user_id: localStorage.getItem("id"),
          token: localStorage.getItem("token"),
          username: localStorage.getItem("username"),
          symbol: JSON.stringify(option),
          watchlist_id: "1",
        };

        const response = await backend.add_symbol(data);

        if (response.error === "False") {
          toast.success(response.message);
          setSelectedOption(null);
          onLoad();
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        console.error("Error adding symbol:", error);
        toast.error("Could not connect to server to add symbol.");
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, onLoad]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.shiftKey && e.key.toLowerCase() === "a") {
        e.preventDefault();
        selectRef.current?.focus(); // Focus react-select input
      }
      if (e.shiftKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        searchInputRef.current?.focus(); // Focus search input
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
  const options = [
    {
      label: "WATCHLIST",
      options: [],
      status: 1,
    },
    {
      label: "NSE-FUTURE",
      options: symbolsList.filter((i) => i.type === "nse"),
      status: nse_future_active,
    },
    {
      label: "NSE-EQUITY",
      options: symbolsList.filter((i) => i.type === "nse_cash"),
      status: nse_cash_active,
    },
    {
      label: "NSE-OPTIONS",
      options: symbolsList.filter((i) => i.type === "fo"),
      status: nse_options_active,
    },
    {
      label: "MCX-FUTURE",
      options: symbolsList.filter((i) => i.type === "mcx"),
      status: mcx_future_active,
    },
    {
      label: "MCX-OPTIONS",
      options: symbolsList.filter((i) => i.type === "mcx_options"),
      status: mcx_options_active,
    },
    {
      label: "COMEX",
      options: symbolsList.filter((i) => i.type === "comex_future"),
      status: comex_active,
    },
    {
      label: "CRYPTO",
      options: symbolsList.filter((i) => i.type === "crypto_future"),
      status: crypto_active,
    },
    {
      label: "FOREX",
      options: symbolsList.filter((i) => i.type === "forex_future"),
      status: forex_active,
    },
    {
      label: "US-STOCK",
      options: symbolsList.filter((i) => i.type === "us_stock"),
      status: us_stock_active,
    },
    {
      label: "SGX",
      options: symbolsList.filter((i) => i.type === "sgx"),
      status: sgx_active,
    },
    {
      label: "OTHERS",
      options: symbolsList.filter((i) => i.type === "index"),
      status: index_active,
    },
    {
      label: "DGCX",
      options: symbolsList.filter((i) => i.type === "dgcx"),
      status: dgcx_active,
    },
    {
      label: "NCDEX",
      options: symbolsList.filter((i) => i.type === "ncdex"),
      status: ncdex_active,
    },
  ];

  const activeSelect = "bg-[var(--primary)] text-[var(--white)]";
  const deActiveSelect =
    "bg-[var(--background)] text-[var(--text)] hover:opacity-80";

  return (
    <header className="h-[55px] bg-[var(--background)]  items-center flex pr-4 pl-4 ">
      <div
        style={{
          flex: 1,
          overflowX: "auto",
          whiteSpace: "nowrap",
          scrollbarWidth: "none", // Firefox
          msOverflowStyle: "none", // IE/Edge
        }}
        className="hide-scrollbar"
      >
        {options
          .filter((item) => item.status !== 0)
          .map((item) => (
            <button
              key={item.label}
              onClick={() => onChangeLink(item.label)}
              className={` ${
                watchlistType === item.label ? activeSelect : deActiveSelect
              } mr-1 text-[13px] text-[var(--text)] relative flex items-center justify-center gap-1 px-2 py-0.3 font-semibold uppercase rounded-md transition-all duration-300 cursor-pointer border-2 border-[var(--hover)] shadow-[0_0_6px_rgba(255,255,255,0.1)]`}
              style={{ display: "inline-block" }}
            >
              {item.label}
            </button>
          ))}
      </div>

      {/* Add + Dropdown */}
      <div className="relative w-[250px] mr-5 bg-[var(--background)]">
        <IoMdAdd
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[18px] z-10"
          style={{ color: "var(--text)" }}
        />

        <Select
          ref={selectRef}
          value={selectedOption}
          onChange={handleChange}
          options={options}
          placeholder="Find symbol..."
          isSearchable
          className="text-[var(--text)]" // ← text theme
          classNamePrefix="rs"
          styles={{
            control: (base) => ({
              ...base,
              borderRadius: "20px",
              paddingLeft: "28px",
              border: "none",
              minHeight: "36px",
              backgroundColor: "var(--hover)", // ← bg theme
              boxShadow: "none",
              color: "var(--text)",
            }),
            placeholder: (base) => ({
              ...base,
              color: "var(--text)",
              opacity: 0.6,
            }),
            menu: (base) => ({
              ...base,
              backgroundColor: "var(--background)",
              overflow: "hidden",
              borderRadius: "12px",
              border: "2px solid var(--hover)", // <— your border
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)", // <— card shadow
              marginTop: "6px",
              padding: "4px",
            }),
            option: (base, { isFocused }) => ({
              ...base,
              backgroundColor: isFocused ? "var(--primary)" : "transparent",
              color: isFocused ? "#fff" : "var(--text)",
              cursor: "pointer",
            }),
            singleValue: (base) => ({
              ...base,
              color: "var(--text)",
            }),
            input: (base) => ({
              ...base,
              color: "var(--text)",
            }),
          }}
          getOptionLabel={(option) =>
            `${option.symbol_display} - ${
              option.type === "nse_equity"
                ? "EQUITY"
                : moment(option.expiry_date).format("DD-MMM")
            }`
          }
          getOptionValue={(option) => option.symbol_id}
        />
      </div>

      {/* Search Input */}
      <div className="relative">
        <FiSearch
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[16px]"
          style={{ color: "var(--text)" }}
        />

        <input
          ref={searchInputRef}
          type="text"
          value={query}
          onChange={handleSearchChange}
          placeholder="Search symbol..."
          className="
      pl-10 pr-3 py-2
      rounded-full
      border-none
      bg-[var(--hover)]
      text-[var(--text)]
      text-sm
      outline-none
      placeholder-opacity-60
    "
        />
      </div>
    </header>
  );
}
